import { useState, type FormEvent } from 'react';
import { motion } from 'motion/react';
import { Logo } from './Logo.js';
import { signIn, signUp } from '../net/account.js';
import { hasAccounts } from '../net/supabase.js';

/**
 * Giriş kapısı.
 *
 * Artık gerçek kimlik doğrulama: hesap Supabase'de açılıyor, oyun sunucusu da
 * imzalı token'ı kendisi doğruluyor. Önceki hâlinde parola (`650`) istemcide
 * karşılaştırılıyordu — derlenmiş pakette görünen, yalnızca kapalı çevreyi
 * ayıklamaya yarayan bir kapıydı.
 *
 * Hesap sistemi yapılandırılmamışsa (anahtarsız geliştirme kopyası) eski
 * "adını yaz ve gir" davranışına düşülüyor; botlara karşı oynamak hesap
 * gerektirmiyor.
 */

interface Props {
  onEnter(name: string): void;
}

export function LoginScreen({ onEnter }: Props) {
  const [mode, setMode] = useState<'in' | 'up'>('in');
  const [username, setUsername] = useState(() => localStorage.getItem('basgan:name') ?? '');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const name = username.trim();

    // Hesapsız kopya: eskisi gibi yalnızca ad alınıyor.
    if (!hasAccounts) {
      if (!name) {
        setError('Bir kullanıcı adı yaz.');
        return;
      }
      localStorage.setItem('basgan:name', name);
      onEnter(name);
      return;
    }

    setBusy(true);
    setError(null);
    const problem =
      mode === 'up'
        ? await signUp(name, password, displayName || name)
        : await signIn(name, password);
    setBusy(false);

    if (problem) {
      setError(problem);
      return;
    }
    // Oturum açıldı; `App` bunu kendi dinleyicisinden görüp devralıyor.
    localStorage.setItem('basgan:name', name.toLowerCase());
  };

  return (
    <div className="gate">
      <div className="gate__felt" aria-hidden />
      <motion.form
        className="gate__card"
        onSubmit={submit}
        initial={{ y: 14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <Logo />

        <label className="field">
          <span>Kullanıcı adı</span>
          <input
            value={username}
            onChange={(e) => {
              setUsername(e.target.value.slice(0, 20));
              setError(null);
            }}
            placeholder="adin"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
          />
        </label>

        {hasAccounts && mode === 'up' && (
          <label className="field">
            <span>Masada görünecek ad</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value.slice(0, 24))}
              placeholder="Alper"
              autoComplete="nickname"
            />
          </label>
        )}

        {hasAccounts && (
          <label className="field">
            <span>Şifre</span>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              placeholder="en az 6 karakter"
              autoComplete={mode === 'up' ? 'new-password' : 'current-password'}
            />
          </label>
        )}

        {error && <p className="error">{error}</p>}

        <button type="submit" className="btn btn--primary btn--pill" disabled={busy}>
          {busy ? 'Bekle…' : mode === 'up' ? 'Hesap aç' : 'Gir'}
        </button>

        {hasAccounts && (
          <button
            type="button"
            className="btn btn--ghost btn--tiny"
            onClick={() => {
              setMode(mode === 'in' ? 'up' : 'in');
              setError(null);
            }}
          >
            {mode === 'in' ? 'Hesabın yok mu? Aç' : 'Hesabın var mı? Gir'}
          </button>
        )}
      </motion.form>
    </div>
  );
}
